/// <amd-module name="Repos/Views/BranchCompare/Components/BranchCompareView" />
import * as React from "react";
import { ChangesExplorerView } from "Repos/Changes/Components/ChangesExplorerView";
import { IChangeCollection } from "Repos/ChangesCommon/Contracts";
import { getChangesForPath } from "Repos/Changes/Util";
import { ChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { CommitsHistoryView } from "Repos/CommitsList/Components/CommitsHistoryView";
import { GitVersionDescriptor } from "Repos/Common/Contracts/Git";
import { getReposRoutingService } from "Repos/Common/ReposRoutingService";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IGitFilesService } from "Repos/Files/GitFilesService";
import { getGitNavigationService } from "Repos/ChangesCommon/ChangesNavigationService";
import { VersionDropdown } from "Repos/VersionDropdown/Components/VersionDropdown";
import { IVersionDropdownItem } from "Repos/VersionDropdown/Components/VersionDropdown.Props";
import * as Resources from "Repos/Views/BranchCompare/Resources";
import { ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { PerformanceScenario, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { Components, useComponentContext } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Button } from "VSSUI/Button";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { IVssContributedTab, TabBar, TabContent } from "VSSUI/Tabs";
import { getBranchCompareService } from "../BranchCompareService";

const hideGraphSettingsKey = "Repos.CommitsHub.HideGraph";

const BranchCompareView = (): JSX.Element => {
    const { pageContext } = useComponentContext();

    const compareService = getBranchCompareService(pageContext);
    const selectedTabId = compareService.getSelectedTabId();
    const baseVersion = compareService.getBaseVersion();
    const targetVersion = compareService.getTargetVersion();

    const filterVisible = new ObservableValue<boolean>(false);
    const showGraph = new ObservableValue(tryGetLocalStorageValue(hideGraphSettingsKey) !== "true");

    const compareTabCommandBarItems = new ObservableArray<IHeaderCommandBarItem>();

    const branchCompareTabs = new ObservableArray<IVssContributedTab>([
        {
            commandBarItems: compareTabCommandBarItems,
            id: "commits",
            name: Resources.Commits,
            render: () => {
                return (
                    <PerformanceScenarioContext.Consumer>
                        {scenarioProps => {
                            const pendingWork = scenarioProps.registerPendingWork("load-file-diff-viewer");
                            return (
                                <div className="page-content page-content-top flex-column flex-grow flex-noshrink rhythm-vertical-16">
                                    <CommitsHistoryView
                                        compareVersion={targetVersion}
                                        filterVisible={filterVisible}
                                        onLoaded={pendingWork!.complete}
                                        shouldUpdateUrl={true}
                                        showGraph={showGraph}
                                        version={baseVersion}
                                    />
                                </div>
                            );
                        }}
                    </PerformanceScenarioContext.Consumer>
                );
            }
        },
        {
            id: "files",
            name: Resources.Files,
            render: () => <BranchCompareFilesTab />
        }
    ]);

    const updateCompareTabHeaderItems = (): void => {
        const graphLabel = showGraph.value ? Resources.GraphShowingTooltip : Resources.GraphHiddenTooltip;
        compareTabCommandBarItems.value = [
            {
                ariaLabel: Resources.ShowFilter,
                iconProps: { iconName: filterVisible.value ? "FilterSolid" : "Filter" },
                id: "filter",
                onActivate: () => {
                    filterVisible.value = !filterVisible.value;
                    updateCompareTabHeaderItems();
                },
                subtle: true
            },
            {
                ariaLabel: graphLabel,
                iconProps: { iconName: "GitGraph" },
                id: "showGraph",
                onActivate: () => {
                    trySetLocalStorageValue(hideGraphSettingsKey, showGraph.value ? "true" : undefined);
                    showGraph.value = !showGraph.value;
                    updateCompareTabHeaderItems();
                },
                text: showGraph.value ? Resources.GraphOn : Resources.GraphOff,
                tooltipProps: { text: graphLabel }
            }
        ];
    };

    updateCompareTabHeaderItems();

    const updateVersions = (baseVersion: GitVersionDescriptor, targetVersion: GitVersionDescriptor) => {
        const repository = getGitRepository(pageContext)!;
        const fpsUrl = getReposRoutingService(pageContext).getBranchCompareUrl(
            repository.name,
            repository.project.name,
            baseVersion,
            targetVersion,
            selectedTabId.value
        );
        FastPageSwitch(pageContext, fpsUrl, true);
    };

    return (
        <PerformanceScenario area="Repos" scenarioName="branch-compare" tti={true}>
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow custom-scrollbar scroll-auto-hide" selectedTabId={selectedTabId} tabProviders={branchCompareTabs}>
                    <Header title={Resources.BranchCompare} titleSize={TitleSize.Large}>
                        <div className="flex-row">
                            <FormatComponent format={Resources.VersionComparing}>
                                <VersionDropdown
                                    initiallySelectedVersion={targetVersion}
                                    onSelect={(ev: any, item: IListBoxItem<IVersionDropdownItem>) => {
                                        updateVersions(baseVersion, item.data!.versionDescriptor);
                                    }}
                                />
                                <VersionDropdown
                                    initiallySelectedVersion={baseVersion}
                                    onSelect={(ev: any, item: IListBoxItem<IVersionDropdownItem>) => {
                                        updateVersions(item.data!.versionDescriptor, targetVersion);
                                    }}
                                />
                            </FormatComponent>
                            <Button
                                iconProps={{ iconName: "Switch" }}
                                onClick={() => {
                                    updateVersions(targetVersion, baseVersion);
                                }}
                                subtle={true}
                            />
                        </div>
                    </Header>
                    <TabBar
                        className="scroll-hidden flex-wrap"
                        onSelectedTabChanged={(newTabId: string) => compareService.setSelectedTabId(newTabId)}
                    />
                    <TabContent />
                </Page>
            </Surface>
        </PerformanceScenario>
    );
};

const BranchCompareFilesTab = (): JSX.Element => {
    const { pageContext } = useComponentContext();
    const compareService = getBranchCompareService(pageContext);
    const filesService = pageContext.getService<IGitFilesService>("IGitFilesService");
    const navigationService = getGitNavigationService(pageContext);

    return (
        <Observer changes={compareService.getChanges()}>
            {(props: { changes: IChangeCollection<GitVersionDescriptor> | undefined }) => {
                if (!props.changes) {
                    return <Spinner />;
                }

                if (getChangesForPath(props.changes, "/").length === 0) {
                    return (
                        <MessageCard className="margin-16" severity={MessageCardSeverity.Info}>
                            {Resources.NoChangesText}
                        </MessageCard>
                    );
                }

                return (
                    <div className="flex-column flex-grow">
                        <ChangesContext.Provider value={{ filesService, navigationService }}>
                            <ChangesExplorerView addDiffKeyboardShortcuts={true} changes={props.changes} />
                        </ChangesContext.Provider>
                    </div>
                );
            }}
        </Observer>
    );
};

Components.add("branchCompareView", BranchCompareView);
